/* eslint-disable no-param-reassign */
/**
 * Popup Behavior Module
 *
 * Provides positioning behaviors (Static, Fixed/Sticky, Smart) for standard Popup forms.
 * This module is independent from the SmartBar StyleBuffer to avoid conflicts and enable
 * isolated testing.
 *
 * Behaviors:
 * - Static: Popup in document flow at top/bottom, does not move with scrolling
 * - Fixed (Sticky): Popup stays fixed at top/bottom of viewport while scrolling
 * - Smart: Popup hides on scroll down and reappears on scroll up
 *
 * Data is stored in element's dataset using `data-popup-behavior-data` attribute.
 */

const _ = require('lodash');

// Show type constants
const SHOW_TYPES = {
  static: 'static',
  fixed: 'fixed',
  smart: 'smart',
};

// Valid positions for behavior modes
const VALID_POSITIONS = ['top', 'bottom'];

// Tracked elements arrays (separate from SmartBar)
let topElements = [];
let bottomElements = [];

// Scroll tracking for smart mode
let lastScrollTop = 0;
let isPopupVisible = true;

// Static bottom mode: observer for legacy cleanup
let staticBottomObserver = null;

// Classes to identify Sumo elements (should be ignored when tracking)
const sumoClasses = [
  'sumome-react-modal-container',
  'sumome-dialog-popup',
  'sumome-dialog-popup-overlay',
  'sumome-image-sharer',
  'sumome-contactform-left-page-hidden',
  'sumome-contactform-right-page-hidden',
  'sumome-react-wysiwyg-component',
  'sumome-smartbar-powered-by-variant',
  'sumo-review-modal',
  'sumo-reply-modal',
  'sumo-question-modal',
  'listbuilder-popup',
  'listbuilder-popup-overlay',
  'listbuilder-popup-content',
];

// ============================================
// DATA STORAGE HELPERS
// ============================================

/**
 * Get popup behavior data from element
 * @param {HTMLElement} el - DOM element
 * @returns {string|null} - JSON string data or null
 */
const getPopupData = (el) => {
  if (!el) return null;
  return el.dataset
    ? el.dataset.popupBehaviorData
    : el.getAttribute('data-popup-behavior-data');
};

/**
 * Set popup behavior data on element
 * @param {HTMLElement} el - DOM element
 * @param {string} val - JSON string value
 */
const setPopupData = (el, val) => {
  if (!el) return;
  if (el.dataset) {
    el.dataset.popupBehaviorData = val;
  } else {
    el.setAttribute('data-popup-behavior-data', val);
  }
};

/**
 * Parse popup data from element
 * @param {HTMLElement} element - DOM element
 * @returns {Object} - Parsed data object or empty object
 */
const parsePopupData = (element) => {
  const popupBehaviorData = getPopupData(element);
  if (!popupBehaviorData) return {};

  let data;
  try {
    data = JSON.parse(popupBehaviorData);
  } catch (e) {
    console.log('JSON parse error:', e);
    data = {};
  }
  return data;
};

// ============================================
// VALIDATION HELPERS
// ============================================

/**
 * Check if position is valid for behavior modes
 * @param {string} position - Position string
 * @returns {boolean} - True if position is 'top' or 'bottom'
 */
const isValidBehaviorPosition = (position) => VALID_POSITIONS.includes(position);

/**
 * Check if element is a Sumo element
 * @param {HTMLElement} element - DOM element
 * @returns {boolean} - True if element is a Sumo element
 */
const isSumoElement = (element) => {
  const className = element.getAttribute('class') || '';
  const classArr = className.split(' ');

  // Check for Sumo badge classes
  if (_.intersection(['s', 'u', 'm', 'o', 'me'], classArr).length === 5) {
    return true;
  }

  // Check for known Sumo classes
  if (_.intersection(sumoClasses, classArr).length) {
    return true;
  }

  return false;
};

/**
 * Check if element is not visible on page
 * @param {HTMLElement} element - DOM element
 * @returns {boolean} - True if element is not visible
 */
const isNotOnPage = (element) => {
  const style = window.getComputedStyle(element);
  if (
    style.getPropertyValue('visibility') === 'hidden'
    || style.getPropertyValue('opacity') === '0'
    || style.getPropertyValue('display') === 'none'
    || element.offsetTop >= document.body.offsetWidth
    || element.offsetLeft + element.offsetWidth <= 0
    || element.offsetLeft >= document.body.offsetWidth
    || element.offsetLeft <= -element.offsetWidth
    || element.offsetTop + element.offsetHeight <= 0
  ) {
    return true;
  }

  const p = element.parentNode;
  if (p && p !== document.body && p !== document.documentElement) {
    return isNotOnPage(p);
  }

  return false;
};

// ============================================
// STYLE MANAGEMENT
// ============================================

/**
 * Store original styles of an element
 * @param {HTMLElement} element - DOM element
 * @param {string} showType - Show type (static, fixed, smart)
 */
const storeOriginalStyles = (element, showType) => {
  const style = window.getComputedStyle(element);
  const existingData = parsePopupData(element);

  // If already has data and using smart/fixed mode, keep existing
  if ((showType === 'smart' || showType === 'fixed') && Object.keys(existingData).length) {
    setPopupData(element, JSON.stringify(_.extend({}, existingData)));
    return;
  }

  const data = {
    originalInlineStyles: {
      top: element.style.top,
      bottom: element.style.bottom,
      marginTop: element.style.marginTop,
      marginBottom: element.style.marginBottom,
      position: element.style.position,
      transition: element.style.transition,
    },
    originalOffset: {
      top: style.getPropertyValue('top'),
      bottom: style.getPropertyValue('bottom'),
    },
    originalOffsetTop: element.offsetTop,
    originalTransition: style.getPropertyValue('transition'),
    originalInlineTransition: element.style.transition,
    originalMarginTop: style.getPropertyValue('margin-top'),
    originalMarginBottom: style.getPropertyValue('margin-bottom'),
  };

  setPopupData(element, JSON.stringify(data));
};

/**
 * Apply original styles to an element
 * @param {HTMLElement} element - DOM element
 * @param {boolean} applyAll - Whether to apply all styles including transition
 */
const applyOriginalStyles = (element, applyAll) => {
  const data = parsePopupData(element);
  if (data && Object.keys(data).length) {
    const originalStyle = data.originalInlineStyles;
    if (originalStyle) {
      _.extend(element.style, originalStyle);
    }

    if (applyAll && data.originalInlineTransition !== undefined) {
      element.style.transition = data.originalInlineTransition;
    }
  }
};

// ============================================
// ELEMENT TRACKING
// ============================================

/**
 * Find and track fixed/absolute elements that need to be pushed
 * @param {HTMLElement} popupEl - Popup element
 * @param {string} position - 'top' or 'bottom'
 * @param {string} showType - Show type
 * @returns {Array} - Array of tracked elements
 */
const findElementsToTrack = (popupEl, position, showType) => {
  const popupHeight = popupEl ? popupEl.offsetHeight : 0;
  const elements = document.body.querySelectorAll('*');

  let trackedElements = _.filter(elements, (element) => {
    const style = window.getComputedStyle(element);
    const elementPosition = style.getPropertyValue('position');
    const display = style.getPropertyValue('display');

    // Skip non-positioned elements
    if (!['fixed', 'absolute'].includes(elementPosition)) {
      return false;
    }

    // Skip hidden elements
    if (display === 'none') return false;

    // Skip the popup itself and Sumo elements
    if (element === popupEl || isSumoElement(element)) {
      return false;
    }

    // Skip elements inside the popup
    let p = element.parentNode;
    while (p && p !== document.body && p !== document.documentElement) {
      if (p === popupEl || isSumoElement(p)) {
        return false;
      }
      p = p.parentNode;
    }

    // For top position, track elements near the top
    if (position === 'top') {
      let { offsetTop } = element;
      if (showType === 'smart' || showType === 'fixed') {
        const data = parsePopupData(element);
        offsetTop = (typeof data.originalOffsetTop !== 'undefined') ? data.originalOffsetTop : offsetTop;
      }

      if (offsetTop + element.offsetHeight >= 0 && offsetTop < popupHeight && offsetTop > -2000) {
        return true;
      }
    }

    // For bottom position, track elements near the bottom
    if (position === 'bottom') {
      if (!isNotOnPage(element)
          && element.offsetTop < document.body.offsetHeight
          && element.offsetTop + element.offsetHeight > document.body.offsetHeight - popupHeight) {
        return true;
      }
    }

    return false;
  });

  // Filter out elements with relative/absolute positioned parents
  if (position === 'top') {
    trackedElements = _.filter(trackedElements, (element) => {
      const style = window.getComputedStyle(element);
      const pos = style.getPropertyValue('position');

      if (pos === 'fixed') return true;

      let p = element.parentNode;
      while (p && p !== document.documentElement && p !== document.body) {
        const parentStyle = window.getComputedStyle(p);
        const parentPosition = parentStyle.getPropertyValue('position');

        if (['relative', 'absolute'].includes(parentPosition)) {
          return false;
        }
        p = p.parentNode;
      }

      return true;
    });
  }

  // Filter out elements whose parents are already tracked
  trackedElements = _.filter(trackedElements, (element) => {
    let p = element.parentNode;
    while (p && p !== document.body && p !== document.documentElement) {
      if (trackedElements.indexOf(p) > -1) {
        return false;
      }
      p = p.parentNode;
    }
    return true;
  });

  return trackedElements;
};

// ============================================
// POPUP WRAPPER HELPERS
// ============================================

/**
 * Get the parent popup wrapper element
 * @param {HTMLElement} popupEl - Popup content element
 * @returns {HTMLElement|null} - Parent wrapper element
 */
const getPopupWrapper = (popupEl) => {
  if (!popupEl) return null;

  // Navigate up to find the listbuilder-popup wrapper
  let wrapper = popupEl.parentNode;
  while (wrapper && wrapper !== document.body) {
    if (wrapper.classList && wrapper.classList.contains('listbuilder-popup')) {
      return wrapper;
    }
    wrapper = wrapper.parentNode;
  }
  return null;
};

/**
 * Get the popup scale element for transform animations
 * @param {HTMLElement} popupEl - Popup content element
 * @returns {HTMLElement|null} - Scale element
 */
const getPopupScaleElement = (popupEl) => {
  if (!popupEl) return null;

  const wrapper = getPopupWrapper(popupEl);
  if (wrapper) {
    return wrapper.querySelector('.listbuilder-popup-scale');
  }
  return null;
};

/**
 * Store original transform of scale element for smart mode animations
 * @param {HTMLElement} popupEl - Popup content element
 */
const storeOriginalScaleTransform = (popupEl) => {
  const scaleEl = getPopupScaleElement(popupEl);
  if (!scaleEl) return;

  // Only store if not already stored
  if (!scaleEl.dataset.originalTransform) {
    scaleEl.dataset.originalTransform = scaleEl.style.transform || '';
  }
};

/**
 * Get stored original transform of scale element
 * @param {HTMLElement} popupEl - Popup content element
 * @returns {string} - Original transform or empty string
 */
const getOriginalScaleTransform = (popupEl) => {
  const scaleEl = getPopupScaleElement(popupEl);
  if (!scaleEl) return '';

  return scaleEl.dataset.originalTransform || scaleEl.style.transform || '';
};

/**
 * Clear stored original transform
 * @param {HTMLElement} popupEl - Popup content element
 */
const clearOriginalScaleTransform = (popupEl) => {
  const scaleEl = getPopupScaleElement(popupEl);
  if (scaleEl && scaleEl.dataset.originalTransform) {
    delete scaleEl.dataset.originalTransform;
  }
};

// ============================================
// APPLY BEHAVIOR - STATIC MODE
// ============================================

/**
 * Clean up static bottom observer (legacy, kept for cleanup safety)
 */
const cleanupStaticBottomObserver = () => {
  if (staticBottomObserver) {
    staticBottomObserver.disconnect();
    staticBottomObserver = null;
  }
};

/**
 * Apply static behavior to popup
 *
 * Since static popups are now truly in document flow (inserted as first/last child
 * of body with position: relative), we only need to adjust fixed/absolute elements
 * that might overlap with the popup. No body margin manipulation is needed.
 *
 * @param {HTMLElement} popupEl - Popup element
 * @param {string} position - 'top' or 'bottom'
 */
const applyStaticBehavior = (popupEl, position) => {
  if (!popupEl) return;

  const popupHeight = popupEl.offsetHeight;

  // Apply original styles to currently tracked elements
  const existingElements = position === 'top' ? topElements : bottomElements;
  _.each(existingElements, (element) => {
    applyOriginalStyles(element);
  });

  // Find fixed/absolute elements that need to be adjusted
  const trackedElements = findElementsToTrack(popupEl, position, 'static');

  if (position === 'top') {
    topElements = trackedElements;

    // Store and push fixed/absolute elements down to avoid overlap
    _.each(topElements, (element) => {
      storeOriginalStyles(element, 'static');
      const data = parsePopupData(element);
      let originalTop = data.originalOffsetTop || 0;

      if (data.originalOffset && data.originalOffset.top && data.originalOffset.top.indexOf('px') > -1) {
        originalTop = parseInt(data.originalOffset.top, 10);
      }

      element.style.top = `${originalTop + popupHeight}px`;
    });
  } else {
    // Bottom position: popup is appended to body end, naturally in document flow
    bottomElements = trackedElements;

    // Push fixed/absolute elements up to avoid overlap
    _.each(bottomElements, (element) => {
      storeOriginalStyles(element, 'static');
      const data = parsePopupData(element);
      const originalTop = data.originalOffsetTop;
      const bottom = document.body.offsetHeight - (originalTop + element.offsetHeight);

      element.style.top = 'auto';
      element.style.bottom = `${bottom + popupHeight}px`;
    });
  }
};

// ============================================
// APPLY BEHAVIOR - FIXED MODE
// ============================================

/**
 * Apply fixed (sticky) behavior to popup
 * @param {HTMLElement} popupEl - Popup element
 * @param {string} position - 'top' or 'bottom'
 */
const applyFixedBehavior = (popupEl, position) => {
  if (!popupEl) return;

  const popupHeight = popupEl.offsetHeight;

  // Find elements to track
  const trackedElements = findElementsToTrack(popupEl, position, 'fixed');

  if (position === 'top') {
    topElements = trackedElements;

    // Store and push elements
    _.each(topElements, (element) => {
      storeOriginalStyles(element, 'fixed');
      const data = parsePopupData(element);
      let originalTop = data.originalOffsetTop || 0;

      if (data.originalOffset && data.originalOffset.top && data.originalOffset.top.indexOf('px') > -1) {
        originalTop = parseInt(data.originalOffset.top, 10);
      }

      element.style.top = `${originalTop + popupHeight}px`;
    });

    // Update body margin
    storeOriginalStyles(document.body, 'fixed');
    const bodyData = parsePopupData(document.body);
    const originalBodyMarginTop = (bodyData.originalMarginTop && bodyData.originalMarginTop.indexOf('px') > -1)
      ? parseInt(bodyData.originalMarginTop, 10)
      : 0;

    document.body.style.marginTop = `${popupHeight + originalBodyMarginTop}px`;
  } else {
    bottomElements = trackedElements;

    // Store and push elements
    _.each(bottomElements, (element) => {
      storeOriginalStyles(element, 'fixed');
      const data = parsePopupData(element);
      const originalTop = data.originalOffsetTop;
      const bottom = document.body.offsetHeight - (originalTop + element.offsetHeight);

      element.style.top = 'auto';
      element.style.bottom = `${bottom + popupHeight}px`;
    });

    // Update body margin
    storeOriginalStyles(document.body, 'fixed');
    const bodyData = parsePopupData(document.body);
    const originalBodyMarginBottom = (bodyData.originalMarginBottom && bodyData.originalMarginBottom.indexOf('px') > -1)
      ? parseInt(bodyData.originalMarginBottom, 10)
      : 0;

    document.body.style.marginBottom = `${popupHeight + originalBodyMarginBottom}px`;
  }
};

// ============================================
// APPLY BEHAVIOR - SMART MODE
// ============================================

/**
 * Apply smart behavior to popup
 * @param {HTMLElement} popupEl - Popup element
 * @param {string} position - 'top' or 'bottom'
 */
const applySmartBehavior = (popupEl, position) => {
  if (!popupEl) return;

  // Store original transform of scale element for animations
  storeOriginalScaleTransform(popupEl);

  const popupHeight = popupEl.offsetHeight;

  // Find elements to track
  const trackedElements = findElementsToTrack(popupEl, position, 'smart');

  if (position === 'top') {
    topElements = trackedElements;

    // Store and push elements with transition
    _.each(topElements, (element) => {
      storeOriginalStyles(element, 'smart');
      const style = window.getComputedStyle(element);
      const transition = style.getPropertyValue('transition');
      const data = parsePopupData(element);
      let originalTop = data.originalOffsetTop || 0;

      if (data.originalOffset && data.originalOffset.top && data.originalOffset.top.indexOf('px') > -1) {
        originalTop = parseInt(data.originalOffset.top, 10);
      }

      // Add transition for smooth animation
      element.style.transition = `${transition ? `${transition}, ` : ''}top .2s ease-in-out`;
      element.style.top = `${originalTop + popupHeight}px`;
    });

    // Update body margin with transition
    storeOriginalStyles(document.body, 'smart');
    const bodyStyle = window.getComputedStyle(document.body);
    const bodyTransition = bodyStyle.getPropertyValue('transition');
    const bodyData = parsePopupData(document.body);
    const originalBodyMarginTop = (bodyData.originalMarginTop && bodyData.originalMarginTop.indexOf('px') > -1)
      ? parseInt(bodyData.originalMarginTop, 10)
      : 0;

    if (document.body.style.transition.indexOf('margin-top .2s ease-in-out') === -1) {
      document.body.style.transition = `${bodyTransition ? `${bodyTransition}, ` : ''}margin-top .2s ease-in-out`;
    }
    document.body.style.marginTop = `${popupHeight + originalBodyMarginTop}px`;
  } else {
    bottomElements = trackedElements;

    // Store and push elements with transition
    _.each(bottomElements, (element) => {
      storeOriginalStyles(element, 'smart');
      const style = window.getComputedStyle(element);
      const transition = style.getPropertyValue('transition');
      const data = parsePopupData(element);
      const originalTop = data.originalOffsetTop;
      const bottom = document.body.offsetHeight - (originalTop + element.offsetHeight);

      // Add transition for smooth animation
      element.style.transition = `${transition}, top .2s ease-in-out, bottom .2s ease-in-out`;
      element.style.top = 'auto';
      element.style.bottom = `${bottom + popupHeight}px`;
    });
  }

  // Initialize scroll tracking
  lastScrollTop = Math.max(document.body.scrollTop, document.documentElement.scrollTop);
  isPopupVisible = true;
};

// ============================================
// SHOW/HIDE POPUP (SMART MODE)
// ============================================

/**
 * Show popup (for smart mode)
 * @param {HTMLElement} popupEl - Popup element
 * @param {string} position - 'top' or 'bottom'
 */
const showPopup = (popupEl, position) => {
  if (!popupEl) return;

  const popupHeight = popupEl.offsetHeight;
  const elements = position === 'top' ? topElements : bottomElements;

  // Remove hidden class from wrapper
  const wrapper = getPopupWrapper(popupEl);
  if (wrapper) {
    wrapper.classList.remove('listbuilder-popup-hidden');
  }

  // Animate the scale element by restoring the original transform
  const scaleEl = getPopupScaleElement(popupEl);
  if (scaleEl) {
    // Get the stored original transform and restore it
    const originalTransform = getOriginalScaleTransform(popupEl);

    // Apply transition and restore original transform
    scaleEl.style.transition = 'transform 0.2s ease-in-out';
    scaleEl.style.transform = originalTransform;
  }

  // Re-push tracked elements
  _.each(elements, (element) => {
    const data = parsePopupData(element);

    if (position === 'top') {
      let originalTop = data.originalOffsetTop || 0;
      if (data.originalOffset && data.originalOffset.top && data.originalOffset.top.indexOf('px') > -1) {
        originalTop = parseInt(data.originalOffset.top, 10);
      }
      element.style.top = `${originalTop + popupHeight}px`;
    } else {
      const originalTop = data.originalOffsetTop;
      const originalBottom = document.body.offsetHeight - (originalTop + element.offsetHeight);
      element.style.bottom = `${originalBottom + popupHeight}px`;
    }
  });

  isPopupVisible = true;
};

/**
 * Hide popup (for smart mode)
 * @param {HTMLElement} popupEl - Popup element
 * @param {string} position - 'top' or 'bottom'
 */
const hidePopup = (popupEl, position) => {
  if (!popupEl) return;

  const elements = position === 'top' ? topElements : bottomElements;

  // Add hidden class to wrapper
  const wrapper = getPopupWrapper(popupEl);
  if (wrapper) {
    wrapper.classList.add('listbuilder-popup-hidden');
  }

  // Animate the scale element by appending translateY to original transform
  const scaleEl = getPopupScaleElement(popupEl);
  if (scaleEl) {
    // Get the stored original transform and append translateY
    const originalTransform = getOriginalScaleTransform(popupEl);
    const translateY = position === 'top' ? 'translateY(-100%)' : 'translateY(100%)';

    // Append translateY to original transform
    const combinedTransform = originalTransform ? `${originalTransform} ${translateY}` : translateY;

    // Apply transition and combined transform
    scaleEl.style.transition = 'transform 0.2s ease-in-out';
    scaleEl.style.transform = combinedTransform;
  }

  // Restore tracked elements to original positions
  _.each(elements, (element) => {
    const style = window.getComputedStyle(element);
    const pos = style.getPropertyValue('position');

    if (position === 'top' && pos === 'fixed') {
      applyOriginalStyles(element);
    } else if (position === 'bottom') {
      const data = parsePopupData(element);
      element.style.bottom = data.originalOffset ? data.originalOffset.bottom : '';
    }
  });

  isPopupVisible = false;
};

// ============================================
// MAIN APPLY BEHAVIOR
// ============================================

/**
 * Apply behavior based on show type
 * @param {HTMLElement} popupEl - Popup element
 * @param {Object} options - Options object { showType, position }
 */
const applyBehavior = (popupEl, options) => {
  if (!popupEl) return;

  const { showType, position } = options || {};

  if (!isValidBehaviorPosition(position)) {
    console.warn(`[popupBehavior] Invalid position: ${position}. Must be 'top' or 'bottom'.`);
    return;
  }

  switch (showType) {
    case SHOW_TYPES.static:
      applyStaticBehavior(popupEl, position);
      break;
    case SHOW_TYPES.fixed:
      applyFixedBehavior(popupEl, position);
      break;
    case SHOW_TYPES.smart:
      applySmartBehavior(popupEl, position);
      break;
    default:
      console.warn(`[popupBehavior] Unknown show type: ${showType}`);
  }
};

// ============================================
// SCROLL HANDLERS
// ============================================

/**
 * Handle scroll for static mode
 * @param {HTMLElement} popupEl - Popup element
 */
const handleStaticScroll = (popupEl) => {
  if (!popupEl) return;

  const delta = Math.max(
    popupEl.offsetHeight - (document.documentElement.scrollTop || document.body.scrollTop),
    0,
  );

  _.each(topElements, (element) => {
    const style = window.getComputedStyle(element);
    const position = style.getPropertyValue('position');
    if (position !== 'fixed') return;

    const data = parsePopupData(element);
    const originalTop = data.originalOffsetTop;
    const newTop = Math.max(popupEl.offsetHeight, originalTop + delta);

    element.style.transition = 'top 500ms';
    setTimeout(() => {
      element.style.top = `${newTop}px`;
    });
  });
};

/**
 * Handle scroll for smart mode
 * @param {HTMLElement} popupEl - Popup element
 * @param {string} position - 'top' or 'bottom'
 */
const handleSmartScroll = (popupEl, position) => {
  if (!popupEl) return;

  const scrollTop = Math.max(document.body.scrollTop, document.documentElement.scrollTop);
  const scrollDelta = scrollTop - lastScrollTop;
  const scrollThreshold = 5; // Minimum scroll distance to trigger

  if (Math.abs(scrollDelta) < scrollThreshold) {
    return;
  }

  if (scrollDelta > 0 && isPopupVisible) {
    // Scrolling down - hide popup
    hidePopup(popupEl, position);
  } else if (scrollDelta < 0 && !isPopupVisible) {
    // Scrolling up - show popup
    showPopup(popupEl, position);
  }

  lastScrollTop = scrollTop;
};

/**
 * Handle scroll based on show type
 * @param {HTMLElement} popupEl - Popup element
 * @param {Object} options - Options object { showType, position }
 */
const handleScroll = (popupEl, options) => {
  if (!popupEl) return;

  const { showType, position } = options || {};

  switch (showType) {
    case SHOW_TYPES.static:
      handleStaticScroll(popupEl);
      break;
    case SHOW_TYPES.smart:
      handleSmartScroll(popupEl, position);
      break;
    case SHOW_TYPES.fixed:
      // Fixed mode doesn't need scroll handling - popup stays visible
      break;
    default:
      break;
  }
};

// ============================================
// RESIZE HANDLER
// ============================================

/**
 * Handle window resize
 * @param {HTMLElement} popupEl - Popup element
 * @param {Object} options - Options object { showType, position }
 */
const handleResize = (popupEl, options) => {
  if (!popupEl) return;

  // Re-apply behavior to recalculate heights
  applyBehavior(popupEl, options);
};

// ============================================
// CLEANUP
// ============================================

/**
 * Remove behavior and restore original styles
 * @param {string} position - 'top' or 'bottom'
 * @param {HTMLElement} [popupEl] - Optional popup element to clear stored transform
 */
const removeBehavior = (position, popupEl) => {
  const elements = position === 'top' ? topElements : bottomElements;

  // Clear stored transform if popupEl is provided (for smart mode animations)
  if (popupEl) {
    clearOriginalScaleTransform(popupEl);
  }

  // Clean up any observers (legacy static bottom observer, if any)
  if (position === 'bottom') {
    cleanupStaticBottomObserver();
  }

  // Restore original styles on tracked fixed/absolute elements
  _.each(elements, (element) => {
    applyOriginalStyles(element);

    // Wait to remove transitions
    setTimeout(() => {
      applyOriginalStyles(element, true);
    }, 500);
  });

  // Restore body styles (for fixed/smart modes that manipulate body margins)
  applyOriginalStyles(document.body, true);

  // Clear tracked elements
  if (position === 'top') {
    topElements = [];
  } else {
    bottomElements = [];
  }

  // Reset scroll tracking
  lastScrollTop = 0;
  isPopupVisible = true;
};

// ============================================
// EXPORTS
// ============================================

module.exports = {
  // Constants
  SHOW_TYPES,
  VALID_POSITIONS,

  // Main functions
  isValidBehaviorPosition,
  applyBehavior,
  handleScroll,
  handleResize,
  showPopup,
  hidePopup,
  removeBehavior,

  // Helpers (exposed for testing)
  getPopupData,
  setPopupData,
  parsePopupData,
};
